import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { ChefLexAvatar } from "../ChefLexAvatar";
import { AlertTriangle, Clock, CreditCard, RefreshCw } from "lucide-react";
import type { LucideIcon } from "lucide-react";

type ErrorType =
  | "GENERATION_FAILED"
  | "PLAN_LIMIT_REACHED"
  | "OPENAI_QUOTA_EXCEEDED"
  | "PAYMENT_FAILED";

interface ChefLexMessageProps {
  type: ErrorType;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  userName?: string;
}

export function ChefLexMessage({
  type,
  onPrimaryAction,
  onSecondaryAction,
  userName = "Alex",
}: ChefLexMessageProps) {
  type ErrorConfig = {
    icon: LucideIcon;
    iconColor: string;
    bgColor: string;
    borderColor: string;
    title: string;
    message: string;
    primaryButton: string;
    primaryAction?: () => void;
    buttonVariant?: "default" | "secondary" | "destructive";
    ghost?: boolean;
  };

  const getErrorConfig = (type: ErrorType): ErrorConfig => {
    switch (type) {
      case "GENERATION_FAILED":
        return {
          icon: AlertTriangle,
          iconColor: "text-warning",
          bgColor: "bg-warning/10",
          borderColor: "border-warning/30",
          title: "Oops, something went scrambled in the kitchen! 🍳",
          message:
            "Don't worry — these things happen! Let's try whipping up your plan again.",
          primaryButton: "Try Again",
          primaryAction: onPrimaryAction,
          buttonVariant: "destructive" as const,
        };

      case "PLAN_LIMIT_REACHED":
        return {
          icon: Clock,
          iconColor: "text-blue-600",
          bgColor: "bg-coral/10",
          borderColor: "border-coral/30",
          title: "You've used this week's plan! Ready for more kitchen magic?",
          message:
            "Level up to Family for 3 plans per week, or Master Chef for unlimited meal planning!",
          primaryButton: "Upgrade to Family",
          primaryAction: onPrimaryAction,
          buttonVariant: "default" as const,
        };

      case "OPENAI_QUOTA_EXCEEDED":
        return {
          icon: Clock,
          iconColor: "text-warning-700",
          bgColor: "bg-warning/10",
          borderColor: "border-warning/30",
          title:
            "The kitchen is extra busy — your plan will be ready in just a few minutes! ⏰",
          message:
            "Our chef AI is handling lots of meal plans right now. Your personalized recipes are worth the wait!",
          primaryButton: "Check Back Soon",
          primaryAction: onSecondaryAction,
          ghost: true,
        };

      case "PAYMENT_FAILED":
        return {
          icon: CreditCard,
          iconColor: "text-red-600",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          title:
            "Looks like there was a payment hiccup — let's get your meal plans flowing again!",
          message:
            "No worries, these things happen! Update your payment method to keep enjoying your personalized meal plans.",
          primaryButton: "Update Payment Method",
          primaryAction: onPrimaryAction,
          buttonVariant: "default" as const,
        };

      default:
        return {
          icon: AlertTriangle,
          iconColor: "text-muted-foreground",
          bgColor: "bg-stone/20",
          borderColor: "border-stone",
          title: "Something unexpected happened",
          message: "Please try again or contact support if the issue persists.",
          primaryButton: "Try Again",
          primaryAction: onPrimaryAction,
        };
    }
  };

  const config = getErrorConfig(type);
  const primaryVariant = config.buttonVariant ?? "primary";

  return (
    <Card
      className={`mx-auto max-w-2xl ${config.borderColor} ${config.bgColor}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <ChefLexAvatar size="md" />
          <div className="flex-1">
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-start gap-3">
                <config.icon
                  className={`h-5 w-5 ${config.iconColor} mt-0.5 flex-shrink-0`}
                />
                <div>
                  <h3 className="mb-2 text-sm font-medium">{config.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {config.message}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                {config.primaryAction && (
                  <Button
                    onClick={config.primaryAction}
                    size="sm"
                    variant={config.ghost ? "ghost" : primaryVariant}
                    className={
                      config.ghost
                        ? "text-muted-foreground hover:text-foreground"
                        : ""
                    }
                  >
                    {config.primaryButton}
                  </Button>
                )}

                {onSecondaryAction && !config.ghost && (
                  <Button
                    onClick={onSecondaryAction}
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
